import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Coins, Loader2, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { api } from "@/lib/api";

const fmtFjd = (n) => {
    const num = Math.round(n || 0);
    return `FJ$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num)}`;
};

/**
 * Compact JV calculator embedded on Hall of Fame closed-deal cards.
 * Pre-filled with the closed deal's `original_asking_fjd` so users see
 * "the same uplift, with your land's value" instantly.
 */
export default function JVCalculatorMini({ defaultPrice = 500000, defaultProjectType = "tourism", caseStudyId = null }) {
    const [askingPrice, setAskingPrice] = useState(defaultPrice);
    const [equityPct, setEquityPct] = useState([25]);
    const [horizon, setHorizon] = useState([10]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const compute = async () => {
        setLoading(true);
        try {
            const multiplier = defaultProjectType === "tourism" ? 2.5
                : defaultProjectType === "commercial" ? 2.0
                    : defaultProjectType === "mixed_use" ? 2.0
                        : defaultProjectType === "residential" ? 1.5
                            : 1.0;
            const data = await api.post("/agent/jv-calculator", {
                asking_price_fjd: Number(askingPrice),
                project_type: defaultProjectType,
                landowner_equity_pct: equityPct[0],
                horizon_years: horizon[0],
                expected_annual_roi_pct: 19,
                capital_multiplier: multiplier,
            });
            setResult(data.data);
        } catch {
            // silently keep last result; user can still play with inputs
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        compute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upliftPct = result?.comparison?.uplift_pct ?? 0;
    const upliftFjd = result?.comparison?.uplift_fjd ?? 0;
    const favored = result?.comparison?.favored ?? "jv";

    return (
        <Card
            data-testid={`hof-mini-calc-${caseStudyId || "default"}`}
            className="p-5 lg:p-6 bg-gradient-to-br from-coral/5 to-fiji-green/5 dark:from-coral/10 dark:to-fiji-green/10 border-coral/30"
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-md bg-coral/15 grid place-items-center">
                    <Calculator className="h-4 w-4 text-coral" />
                </div>
                <div>
                    <div className="font-display text-sm font-bold text-foreground">Try it with your land's value</div>
                    <p className="font-app text-[11px] text-muted-foreground leading-snug">
                        Same model, your numbers. Live calculation — no signup needed.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="sm:col-span-3">
                    <Label className="text-[10px] uppercase tracking-wider font-app text-muted-foreground mb-1 block">
                        Your asking price (FJD)
                    </Label>
                    <Input
                        type="number"
                        value={askingPrice}
                        onChange={(e) => setAskingPrice(Number(e.target.value))}
                        min={1000}
                        step={1000}
                        data-testid={`hof-mini-calc-price-${caseStudyId || "default"}`}
                        className="bg-background text-foreground font-app text-base font-semibold"
                    />
                </div>
                <div className="sm:col-span-3 grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-app text-muted-foreground mb-1 block">
                            Your equity:&nbsp;
                            <span className="font-bold text-foreground tabular-nums">{equityPct[0]}%</span>
                        </Label>
                        <Slider
                            value={equityPct}
                            onValueChange={setEquityPct}
                            min={5}
                            max={50}
                            step={1}
                            data-testid={`hof-mini-calc-equity-${caseStudyId || "default"}`}
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-app text-muted-foreground mb-1 block">
                            Horizon:&nbsp;
                            <span className="font-bold text-foreground tabular-nums">{horizon[0]}y</span>
                        </Label>
                        <Slider
                            value={horizon}
                            onValueChange={setHorizon}
                            min={3}
                            max={20}
                            step={1}
                            data-testid={`hof-mini-calc-horizon-${caseStudyId || "default"}`}
                        />
                    </div>
                </div>
            </div>

            <Button
                onClick={compute}
                disabled={loading}
                size="sm"
                data-testid={`hof-mini-calc-recompute-${caseStudyId || "default"}`}
                className="w-full bg-coral hover:bg-coral-deep text-white font-app mb-3"
            >
                {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Coins className="h-3.5 w-3.5 mr-1.5" />}
                Recalculate
            </Button>

            {result && (
                <div className="rounded-md bg-fiji-green/10 dark:bg-fiji-green/15 border border-fiji-green/30 p-3 mb-3" data-testid={`hof-mini-calc-result-${caseStudyId || "default"}`}>
                    <div className="flex items-baseline gap-2 mb-1">
                        <Sparkles className="h-3.5 w-3.5 text-coral shrink-0" />
                        {favored === "jv" ? (
                            <div className="font-display text-sm sm:text-base font-bold text-foreground leading-snug">
                                JV beats sale by{" "}
                                <span className="text-fiji-green tabular-nums">+{upliftPct}%</span>{" "}
                                ({fmtFjd(Math.abs(upliftFjd))})
                            </div>
                        ) : (
                            <div className="font-display text-sm sm:text-base font-bold text-foreground leading-snug">
                                Sale wins for this scenario by{" "}
                                <span className="text-coral tabular-nums">{Math.abs(upliftPct)}%</span>
                            </div>
                        )}
                    </div>
                    <p className="font-app text-[11px] text-muted-foreground leading-snug pl-5">
                        Over {result.inputs.horizon_years} years · {result.inputs.project_type} model.
                    </p>
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" data-testid={`hof-mini-calc-full-btn-${caseStudyId || "default"}`} className="bg-ocean-deep hover:bg-ocean-deep/90 text-white font-app text-xs flex-1 min-w-[140px]">
                    <Link to={`/agents/jv-calculator?price=${askingPrice}&type=${defaultProjectType}`}>
                        Open full calculator <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid={`hof-mini-calc-jv-cta-${caseStudyId || "default"}`} className="font-app text-xs flex-1 min-w-[140px]">
                    <Link to="/jv/submit">
                        Submit my land
                    </Link>
                </Button>
            </div>
        </Card>
    );
}
